import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const NAV_LINKS = (t) => [
  { href: "#home", label: t.nav.home },
  { href: "#events", label: t.nav.events },
  { href: "#branding", label: t.nav.branding },
  { href: "#pricing", label: t.nav.pricing },
  { href: "#faq", label: t.nav.faq },
  { href: "#contact", label: t.nav.contact },
];

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = NAV_LINKS(t);

  return (
    <header
      data-testid="fp-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
        <a
          href="#home"
          data-testid="fp-logo"
          className="flex items-center gap-2 font-heading font-bold text-xl md:text-2xl tracking-tight text-neutral-900"
        >
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-[#0055FF] text-white text-sm">
            FP
          </span>
          FootPool
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-neutral-700 hover:text-[#0055FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1 p-1 rounded-full border border-slate-200 bg-white/70 backdrop-blur">
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-switch-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all ${
                  lang === l.code
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-500 hover:text-neutral-900"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href="#contact"
            data-testid="nav-cta-quote"
            className="hidden md:inline-flex items-center rounded-full bg-[#0055FF] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0044dd] transition-all shadow-lg shadow-[#0055FF]/20"
          >
            {t.nav.cta}
          </a>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="fp-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="px-5 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                className="text-base font-medium text-neutral-800"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-2">
              {languages.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                    lang === l.code
                      ? "bg-neutral-900 text-white"
                      : "bg-slate-100 text-neutral-700"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-quote"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[#0055FF] text-white px-5 py-3 text-sm font-semibold"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
